import React, { useState, useRef } from "react";
import {
  X,
  Upload,
  FileSpreadsheet,
  Download,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Copy,
  ArrowLeft,
  Send,
} from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { invitesApi } from "../lib/api";
import { formatApiErrorDetail } from "../contexts/AuthContext";

const MAX_ROWS = 100;
const VALID_ROLES = new Set(["admin", "dispatcher", "technician", "office_personnel"]);

const STATUS_STYLE = {
  invited: "bg-emerald-50 text-emerald-700 border-emerald-200",
  skipped: "bg-amber-50 text-amber-700 border-amber-200",
  error: "bg-red-50 text-red-700 border-red-200",
};
const STATUS_ICON = {
  invited: CheckCircle2,
  skipped: AlertCircle,
  error: XCircle,
};

const TEMPLATE_CSV = `email,role,name
alice@yourcompany.com,dispatcher,Alice Walker
bob@yourcompany.com,technician,Bob Reyes
chen@yourcompany.com,admin,Chen Park
`;

/** Tolerant CSV parser — handles quoted fields with commas, CRLF/LF, BOM. */
function parseCsv(text) {
  const stripped = text.replace(/^\uFEFF/, "");
  const rows = [];
  let cur = "";
  let row = [];
  let inQuotes = false;
  for (let i = 0; i < stripped.length; i++) {
    const ch = stripped[i];
    if (inQuotes) {
      if (ch === '"') {
        if (stripped[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      row.push(cur);
      cur = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && stripped[i + 1] === "\n") i++;
      row.push(cur);
      rows.push(row);
      row = [];
      cur = "";
    } else {
      cur += ch;
    }
  }
  if (cur.length > 0 || row.length > 0) {
    row.push(cur);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c && c.trim().length > 0));
}

function normalizeRows(rawRows) {
  if (rawRows.length === 0) return { rows: [], headerError: "Empty file" };
  // Detect header
  const first = rawRows[0].map((c) => (c || "").trim().toLowerCase());
  const hasHeader = first.includes("email");
  const headerMap = {};
  if (hasHeader) {
    first.forEach((h, idx) => {
      if (h === "email") headerMap.email = idx;
      else if (h === "role") headerMap.role = idx;
      else if (h === "name") headerMap.name = idx;
    });
    if (headerMap.email === undefined) {
      return { rows: [], headerError: "Header row must include 'email' column" };
    }
  } else {
    headerMap.email = 0;
    headerMap.role = 1;
    headerMap.name = 2;
  }
  const dataRows = hasHeader ? rawRows.slice(1) : rawRows;
  const seen = new Set();
  const rows = dataRows.map((r, idx) => {
    const email = (r[headerMap.email] || "").trim().toLowerCase();
    const role = (headerMap.role !== undefined ? r[headerMap.role] || "" : "")
      .trim()
      .toLowerCase() || "dispatcher";
    const name = (headerMap.name !== undefined ? r[headerMap.name] || "" : "").trim();
    const errors = [];
    if (!email) errors.push("missing email");
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) errors.push("invalid email");
    if (role && !VALID_ROLES.has(role)) errors.push(`invalid role "${role}"`);
    if (email && seen.has(email)) errors.push("duplicate in file");
    if (email) seen.add(email);
    return { idx: idx + (hasHeader ? 2 : 1), email, role, name: name || undefined, errors };
  });
  return { rows, headerError: null };
}

export default function BulkInviteModal({ onClose, onComplete }) {
  const fileRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState([]); // [{idx, email, role, name?, errors[]}]
  const [headerError, setHeaderError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null); // {summary, results}
  const [dragOver, setDragOver] = useState(false);

  const validCount = rows.filter((r) => r.errors.length === 0).length;
  const invalidCount = rows.length - validCount;

  const ingestText = (text, name) => {
    const parsed = parseCsv(text);
    const { rows: norm, headerError: err } = normalizeRows(parsed);
    setFileName(name);
    setHeaderError(err || "");
    setRows(norm);
    if (norm.length > MAX_ROWS) {
      toast.error(`File contains ${norm.length} rows — only the first ${MAX_ROWS} will be sent`);
    }
  };

  const onFile = (file) => {
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) {
      toast.error("Please upload a .csv file");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => ingestText(String(e.target?.result || ""), file.name);
    reader.onerror = () => toast.error("Failed to read file");
    reader.readAsText(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    onFile(e.dataTransfer.files?.[0]);
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_CSV], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "servicepro-invite-template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const submit = async () => {
    const valid = rows.filter((r) => r.errors.length === 0).slice(0, MAX_ROWS);
    if (valid.length === 0) {
      toast.error("No valid rows to send");
      return;
    }
    setSubmitting(true);
    try {
      const payload = valid.map((r) => ({ email: r.email, role: r.role, name: r.name }));
      const res = await invitesApi.bulk(payload);
      setResults(res);
      onComplete && onComplete(res);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const copyAllLinks = async () => {
    const invited = (results?.results || []).filter((r) => r.status === "invited" && r.accept_url);
    if (invited.length === 0) {
      toast.error("No successful invites to copy");
      return;
    }
    const text = invited.map((r) => `${r.email}\t${r.accept_url}`).join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Copied ${invited.length} magic links (tab-separated)`);
    } catch (_) {
      toast.error("Copy failed");
    }
  };

  const reset = () => {
    setFileName("");
    setRows([]);
    setHeaderError("");
    setResults(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-6">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col border border-slate-200">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-fuchsia-50 via-blue-50 to-cyan-50">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4 text-blue-600" />
            <div className="text-[14px] font-semibold text-slate-900">
              Bulk invite via CSV
              {results && (
                <span className="ml-2 text-[11px] text-slate-500 font-normal">— results</span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="bulk-invite-close"
            className="h-7 w-7 rounded hover:bg-white/60 flex items-center justify-center"
          >
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>

        {!results ? (
          <>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {/* Drop zone */}
              <label
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                data-testid="bulk-invite-dropzone"
                className={`flex flex-col items-center justify-center gap-2 px-6 py-8 rounded-xl border-2 border-dashed cursor-pointer transition-colors ${
                  dragOver ? "border-blue-400 bg-blue-50/50" : "border-slate-200 hover:border-slate-300 bg-slate-50/40"
                }`}
              >
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => onFile(e.target.files?.[0])}
                  className="hidden"
                  data-testid="bulk-invite-file-input"
                />
                <Upload className="h-7 w-7 text-slate-400" />
                <div className="text-[13.5px] font-semibold text-slate-700">
                  Drop a CSV here, or click to upload
                </div>
                <div className="text-[11.5px] text-slate-500 text-center max-w-md">
                  Expected columns: <code className="font-mono text-slate-700">email, role, name</code> (role & name optional).
                  Up to {MAX_ROWS} rows per batch.
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    downloadTemplate();
                  }}
                  data-testid="bulk-invite-template-button"
                  className="mt-1 inline-flex items-center gap-1.5 text-[11.5px] text-blue-600 hover:text-blue-700 font-semibold"
                >
                  <Download className="h-3.5 w-3.5" /> Download template
                </button>
              </label>

              {/* Header error */}
              {headerError && (
                <div data-testid="bulk-invite-header-error" className="px-3 py-2 rounded-md bg-red-50 border border-red-200 text-[12.5px] text-red-700">
                  {headerError}
                </div>
              )}

              {/* Preview table */}
              {rows.length > 0 && (
                <div data-testid="bulk-invite-preview" className="border border-slate-200 rounded-xl overflow-hidden">
                  <div className="px-4 py-2 flex items-center justify-between border-b border-slate-100 bg-slate-50/60 text-[12px]">
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-slate-800">{fileName}</span>
                      <span className="text-slate-500">·</span>
                      <span className="text-slate-600">{rows.length} rows</span>
                      <span className="text-emerald-600 font-medium">{validCount} valid</span>
                      {invalidCount > 0 && (
                        <span className="text-red-600 font-medium">{invalidCount} invalid</span>
                      )}
                    </div>
                    <button onClick={reset} className="text-[11.5px] text-slate-500 hover:text-slate-800">
                      Clear
                    </button>
                  </div>
                  <div className="max-h-[280px] overflow-y-auto">
                    <table className="w-full text-[12.5px]" data-testid="bulk-invite-preview-table">
                      <thead className="bg-slate-50/40 text-[10.5px] uppercase tracking-wider text-slate-500 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2 font-semibold w-10">#</th>
                          <th className="text-left px-3 py-2 font-semibold">Email</th>
                          <th className="text-left px-3 py-2 font-semibold w-28">Role</th>
                          <th className="text-left px-3 py-2 font-semibold">Name</th>
                          <th className="text-left px-3 py-2 font-semibold w-44">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {rows.map((r) => (
                          <tr
                            key={r.idx}
                            className={r.errors.length > 0 ? "bg-red-50/40" : ""}
                          >
                            <td className="px-3 py-1.5 text-slate-400 font-mono text-[11px]">{r.idx}</td>
                            <td className="px-3 py-1.5 text-slate-800">{r.email || <em className="text-slate-400">—</em>}</td>
                            <td className="px-3 py-1.5 capitalize text-slate-600">{r.role}</td>
                            <td className="px-3 py-1.5 text-slate-600">{r.name || <em className="text-slate-400">—</em>}</td>
                            <td className="px-3 py-1.5">
                              {r.errors.length === 0 ? (
                                <span className="inline-flex items-center gap-1 text-emerald-600 text-[11.5px] font-medium">
                                  <CheckCircle2 className="h-3.5 w-3.5" /> ready
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-red-600 text-[11.5px] font-medium">
                                  <XCircle className="h-3.5 w-3.5" /> {r.errors.join("; ")}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="text-[11px] text-slate-500 leading-relaxed">
                Existing pending invites for the same email will be revoked & re-sent. Users that already exist will be
                skipped. Each invite is a one-click magic link valid for 72 hours.
              </div>
            </div>

            <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between bg-slate-50/40">
              <div className="text-[12px] text-slate-500">
                {validCount > 0 ? (
                  <>
                    <strong className="text-slate-800">{Math.min(validCount, MAX_ROWS)}</strong> invitation
                    {validCount === 1 ? "" : "s"} ready to send
                  </>
                ) : (
                  "Upload a CSV to preview rows"
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={onClose} className="h-9 text-[13px]">
                  Cancel
                </Button>
                <Button
                  onClick={submit}
                  disabled={submitting || validCount === 0}
                  data-testid="bulk-invite-submit"
                  className="h-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[13px]"
                >
                  <Send className="h-4 w-4 mr-1.5" />
                  {submitting ? "Sending…" : `Send ${Math.min(validCount, MAX_ROWS)} invitations`}
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Results screen */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="bulk-invite-results">
              <div className="grid grid-cols-4 gap-2.5">
                <SummaryCard label="Total" value={results.summary.total} tint="slate" />
                <SummaryCard label="Invited" value={results.summary.invited} tint="emerald" />
                <SummaryCard label="Skipped" value={results.summary.skipped} tint="amber" />
                <SummaryCard label="Errors" value={results.summary.errors} tint="red" />
              </div>

              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <div className="max-h-[320px] overflow-y-auto">
                  <table className="w-full text-[12.5px]" data-testid="bulk-invite-results-table">
                    <thead className="bg-slate-50/60 text-[10.5px] uppercase tracking-wider text-slate-500 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold">Email</th>
                        <th className="text-left px-3 py-2 font-semibold w-28">Role</th>
                        <th className="text-left px-3 py-2 font-semibold w-32">Status</th>
                        <th className="text-left px-3 py-2 font-semibold">Detail</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {results.results.map((r, i) => {
                        const Icon = STATUS_ICON[r.status] || XCircle;
                        return (
                          <tr key={`${r.email}-${i}`}>
                            <td className="px-3 py-1.5 text-slate-800">{r.email}</td>
                            <td className="px-3 py-1.5 capitalize text-slate-600">{r.role || "—"}</td>
                            <td className="px-3 py-1.5">
                              <span
                                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-[11px] font-semibold capitalize ${STATUS_STYLE[r.status]}`}
                              >
                                <Icon className="h-3 w-3" /> {r.status}
                              </span>
                            </td>
                            <td className="px-3 py-1.5 text-slate-500">
                              {r.status === "invited" ? (
                                r.email_sent ? (
                                  <span className="text-emerald-600">Email sent</span>
                                ) : (
                                  <span className="text-amber-700">Email pending — link in Pending Invitations</span>
                                )
                              ) : (
                                r.reason || "—"
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between bg-slate-50/40">
              <Button variant="outline" onClick={reset} className="h-9 text-[13px]" data-testid="bulk-invite-back">
                <ArrowLeft className="h-4 w-4 mr-1.5" /> Send another batch
              </Button>
              <div className="flex items-center gap-2">
                {results.summary.invited > 0 && (
                  <Button
                    variant="outline"
                    onClick={copyAllLinks}
                    data-testid="bulk-invite-copy-all"
                    className="h-9 text-[13px]"
                  >
                    <Copy className="h-4 w-4 mr-1.5" /> Copy all magic links
                  </Button>
                )}
                <Button
                  onClick={onClose}
                  data-testid="bulk-invite-done"
                  className="h-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[13px]"
                >
                  Done
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, tint }) {
  const tintMap = {
    slate: "from-slate-50 to-white border-slate-200 text-slate-700",
    emerald: "from-emerald-50 to-white border-emerald-200 text-emerald-700",
    amber: "from-amber-50 to-white border-amber-200 text-amber-700",
    red: "from-red-50 to-white border-red-200 text-red-700",
  };
  return (
    <div className={`bg-gradient-to-br rounded-xl border p-3 ${tintMap[tint]}`}>
      <div className="text-[10.5px] font-semibold uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-[22px] font-bold text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}
